import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router';
import {
  AlertCircle,
  CheckCircle2,
  CircleDashed,
  Gauge,
  Pause,
  Play,
  XCircle,
} from 'lucide-react';
import {
  type CollectorCadenceState,
  type CollectorErrorClass,
  type CollectorHealth,
  type CollectorStatus,
  type ConnectionMethodHealth,
  useDeviceHealth,
  useTriggerCollector,
} from '@/hooks/useDeviceHealth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface DeviceHealthProps {
  deviceId: string;
}

const STATUS_STYLE: Record<CollectorStatus, string> = {
  healthy: 'bg-sev-4/15 text-sev-4',
  degraded: 'bg-sev-3/20 text-foreground',
  failing: 'bg-sev-1/15 text-sev-1',
  cooldown: 'bg-sev-2/15 text-sev-2',
};

const STATUS_ICON: Record<CollectorStatus, typeof CheckCircle2> = {
  healthy: CheckCircle2,
  degraded: CircleDashed,
  failing: XCircle,
  cooldown: Pause,
};

const ERROR_CLASS_LABEL: Record<CollectorErrorClass, string> = {
  auth: 'Authentication',
  network: 'Network',
  parse: 'Parse',
  unknown: 'Unknown',
};

const SESSION_TYPE_LABEL: Record<string, string> = {
  ssh: 'SSH',
  telnet: 'Telnet',
  http: 'HTTP',
  https: 'HTTPS',
  console: 'Console',
  netconf: 'NETCONF',
};

function statusFromMethod(
  method: ConnectionMethodHealth,
): { label: CollectorStatus; tone: string } {
  // Derive a coarse status for the connection method from its
  // success/failure timestamps. Mirrors the collector_health enum
  // for visual consistency in the same screen.
  if (!method.enabled) {
    return { label: 'cooldown', tone: 'Disabled' };
  }
  if (method.successCount === 0 && method.failureCount === 0) {
    return { label: 'degraded', tone: 'No data yet' };
  }
  const lastSuccess = method.lastSuccess ? Date.parse(method.lastSuccess) : 0;
  const lastFailure = method.lastFailure ? Date.parse(method.lastFailure) : 0;
  // Most-recent outcome wins. If the last thing we saw was a
  // failure — regardless of historical successes — treat as failing.
  if (lastFailure > lastSuccess) {
    return { label: 'failing', tone: 'Last attempt failed' };
  }
  return { label: 'healthy', tone: 'Last attempt succeeded' };
}

function StatusChip({ status, label }: { status: CollectorStatus; label?: string }) {
  const Icon = STATUS_ICON[status];
  return (
    <Badge className={cn('border-0 font-normal capitalize gap-1', STATUS_STYLE[status])}>
      <Icon className="w-3 h-3" aria-hidden="true" />
      {label ?? status}
    </Badge>
  );
}

function relativeOrDash(ts: string | null): string {
  if (!ts) return '—';
  return formatDistanceToNow(new Date(ts), { addSuffix: true });
}

/** Compact run duration: sub-second stays in ms, otherwise seconds. */
function formatRunDuration(ms: number): string {
  if (ms < 1000) return `${String(Math.round(ms))}ms`;
  const seconds = ms / 1000;
  return seconds < 10 ? `${seconds.toFixed(1)}s` : `${String(Math.round(seconds))}s`;
}

/**
 * How much of each interval the device spends answering this collector.
 *
 * The number that matters, and the one an interval alone cannot express: a
 * collector whose run approaches its own interval is effectively always on the
 * device while still reporting that interval. Null when either half is
 * unmeasured — an older gateway reports neither.
 */
function dutyCycle(row: CollectorHealth): number | null {
  if (row.lastDurationMs == null || !row.effectiveIntervalSeconds) return null;
  return row.lastDurationMs / (row.effectiveIntervalSeconds * 1000);
}

/**
 * What each non-normal cadence state means, in the terms the user cares about:
 * whether they are losing anything.
 *
 * `over_budget_at_floor` deliberately reads as a statement about the device
 * rather than a warning about the data — the floor was held, so nothing was
 * lost; the device simply costs more to observe correctly than the budget
 * allows. `counters_reduced` is the opposite: it is the one state that does
 * cost visibility, so it says so plainly.
 */
const CADENCE_STATE_NOTE: Record<
  Exclude<CollectorCadenceState, 'normal'>,
  { label: string; detail: string; tone: string }
> = {
  structural_deferred: {
    label: 'Structural refresh deferred',
    detail:
      'Slow-changing details (port names, chassis inventory) are being refreshed less often to ease load. Counters and status are unaffected.',
    tone: 'bg-sev-3/20 text-foreground',
  },
  counters_reduced: {
    label: 'Counter cadence reduced',
    detail:
      'Counters are being sampled less often than configured to keep this device responsive. Rates stay correct, but they are coarser than usual.',
    tone: 'bg-sev-2/15 text-sev-2',
  },
  over_budget_at_floor: {
    label: 'Costly to poll',
    detail:
      'This collector costs more than its load budget allows at the cadence its data requires. Cadence was held so no data is lost — the device is undersized for what we ask of it, or its polling is not yet optimised.',
    tone: 'bg-sev-2/15 text-sev-2',
  },
};

/**
 * What one run costs the device. Rendered only when the reporting gateway
 * actually measured it: every field is null on an older gateway, and that is
 * "not measured", not "free".
 */
export function ProbeCostLine({ row }: { row: CollectorHealth }) {
  const duty = dutyCycle(row);
  const parts: string[] = [];

  if (row.lastDurationMs != null) {
    parts.push(
      row.effectiveIntervalSeconds
        ? `Last run ${formatRunDuration(row.lastDurationMs)} of ${String(row.effectiveIntervalSeconds)}s`
        : `Last run ${formatRunDuration(row.lastDurationMs)}`,
    );
  }
  if (row.lastPdus != null) {
    parts.push(`${row.lastPdus.toLocaleString()} request${row.lastPdus === 1 ? '' : 's'}`);
  }
  // Only when a session was actually opened: for SNMP that is a reconnect, and
  // for NETCONF/SSH it is a process spawn on the device — the cost that neither
  // bytes nor request count reflects.
  if (row.lastSessionOpens) {
    parts.push(
      `${String(row.lastSessionOpens)} session${row.lastSessionOpens === 1 ? '' : 's'} opened`,
    );
  }

  if (parts.length === 0) return null;

  return (
    <div className="text-xs text-muted-foreground">
      {parts.join(' · ')}
      {duty != null ? (
        <>
          {' · '}
          {/* Half the interval is where the overrun guard starts holding a
              collector back, so it is the threshold worth drawing the eye to. */}
          <span className={cn(duty >= 0.5 && 'text-sev-2 font-medium')}>
            {Math.round(duty * 100)}% of interval
          </span>
        </>
      ) : null}
    </div>
  );
}

/** The gateway-identifying fields shared by both health row types. */
interface GatewayMeta {
  gatewayId: string;
  gatewayName: string | null;
  gatewayStatus: string;
}

function gatewayLabel(meta: GatewayMeta): string {
  return meta.gatewayName ?? `Gateway ${meta.gatewayId.slice(0, 8)}`;
}

const GATEWAY_STATUS_STYLE: Record<string, string> = {
  online: 'bg-sev-4/15 text-sev-4',
  offline: 'bg-sev-1/15 text-sev-1',
  unknown: 'bg-muted text-muted-foreground',
};

function GatewayStatusBadge({ status }: { status: string }) {
  const style = GATEWAY_STATUS_STYLE[status.toLowerCase()] ?? GATEWAY_STATUS_STYLE.unknown;
  return <Badge className={cn('border-0 font-normal capitalize', style)}>{status}</Badge>;
}

/**
 * De-dupe gateways out of a set of health rows and order them
 * online-first, then by name. More than one gateway per device happens
 * in true HA (two gateways in a region both polling it); grouping by
 * gateway keeps each one's collectors legible instead of interleaving
 * identically-named rows. Soft-deleted gateways are already filtered
 * server-side, so anything here is a live gateway.
 */
function orderedGateways(rows: GatewayMeta[]): GatewayMeta[] {
  const byId = new Map<string, GatewayMeta>();
  for (const r of rows) {
    if (!byId.has(r.gatewayId)) {
      byId.set(r.gatewayId, {
        gatewayId: r.gatewayId,
        gatewayName: r.gatewayName,
        gatewayStatus: r.gatewayStatus,
      });
    }
  }
  return Array.from(byId.values()).sort((a, b) => {
    const ao = a.gatewayStatus.toLowerCase() === 'online' ? 0 : 1;
    const bo = b.gatewayStatus.toLowerCase() === 'online' ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return gatewayLabel(a).localeCompare(gatewayLabel(b));
  });
}

function GatewayGroupHeader({ meta }: { meta: GatewayMeta }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Link
        to={`/devices/${meta.gatewayId}`}
        className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
      >
        {gatewayLabel(meta)}
      </Link>
      <GatewayStatusBadge status={meta.gatewayStatus} />
    </div>
  );
}

function CollectorRow({ row, deviceId }: { row: CollectorHealth; deviceId: string }) {
  const trigger = useTriggerCollector(deviceId);
  // Controller collectors have no trigger path — their data arrives on the
  // integration's own sync, not from anything the gateway can be asked to run,
  // so the POST would only ever fail. Offering the button would be offering an
  // action that does not exist.
  const canTriggerNow = !row.collectorName.startsWith('controller_');
  // Any non-normal cadence is shown. The design commits to never silently
  // trading away visibility — which also means never silently keeping it.
  const cadenceNote =
    row.cadenceState && row.cadenceState !== 'normal'
      ? CADENCE_STATE_NOTE[row.cadenceState]
      : null;
  return (
    <li className="rounded-lg border border-border bg-card p-4 space-y-2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-sm">{row.collectorName}</div>
          <div className="text-xs text-muted-foreground">
            Last success: {relativeOrDash(row.lastSuccessAt)}
            {row.lastFailureAt ? (
              <>
                {' '}· Last failure: {relativeOrDash(row.lastFailureAt)}
              </>
            ) : null}
          </div>
          {row.nextCollectionAt ? (
            <div className="text-xs text-muted-foreground">
              {/* Future timestamp — relativeOrDash renders "in N min". While in
                  cooldown this is the cooldown end, so the same line answers
                  "when does polling resume". */}
              {row.status === 'cooldown' ? 'Resumes' : 'Next collection'}:{' '}
              {relativeOrDash(row.nextCollectionAt)}
            </div>
          ) : null}
          <ProbeCostLine row={row} />
        </div>
        <StatusChip status={row.status} />
      </div>
      {row.consecutiveFailures > 0 ? (
        <div className="text-xs text-muted-foreground">
          {row.consecutiveFailures} consecutive failure
          {row.consecutiveFailures === 1 ? '' : 's'}
          {row.lastErrorClass ? (
            <> · {ERROR_CLASS_LABEL[row.lastErrorClass]}</>
          ) : null}
        </div>
      ) : null}
      {cadenceNote ? (
        <div className="space-y-1">
          <Badge className={cn('border-0 font-normal gap-1', cadenceNote.tone)}>
            <Gauge className="w-3 h-3" aria-hidden="true" />
            {cadenceNote.label}
          </Badge>
          <div className="text-xs text-muted-foreground">{cadenceNote.detail}</div>
        </div>
      ) : null}
      {row.lastErrorMessage && row.status !== 'healthy' ? (
        <div className="text-xs font-mono bg-bg-subtle border border-border-subtle rounded px-2 py-1.5 break-words whitespace-pre-wrap">
          {row.lastErrorMessage}
        </div>
      ) : null}
      {canTriggerNow ? (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            disabled={trigger.isPending}
            onClick={() => { trigger.mutate(row.collectorName); }}
            // Runs this collector immediately, bypassing schedule and cooldown.
            title="Run this collector now, ignoring schedule and cooldown"
          >
            <Play className="w-3 h-3" />
            {trigger.isPending ? 'Running…' : 'Run now'}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function ConnectionMethodRow({ row }: { row: ConnectionMethodHealth }) {
  const { label, tone } = statusFromMethod(row);
  const protoLabel = SESSION_TYPE_LABEL[row.sessionType] ?? row.sessionType;
  const target = [row.host, row.port ? `:${String(row.port)}` : ''].filter(Boolean).join('');
  return (
    <li className="rounded-lg border border-border bg-card p-4 space-y-2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-sm flex items-baseline gap-2 flex-wrap">
            <span>{protoLabel}</span>
            {target ? (
              <span className="text-muted-foreground font-normal font-mono text-xs">
                {target}
              </span>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            Last success: {relativeOrDash(row.lastSuccess)}
            {row.lastFailure ? (
              <>
                {' '}· Last failure: {relativeOrDash(row.lastFailure)}
              </>
            ) : null}
            {row.avgLatencyMs !== null ? (
              <>
                {' '}· Avg latency: {row.avgLatencyMs} ms
              </>
            ) : null}
          </div>
        </div>
        <StatusChip status={label} label={tone} />
      </div>
      <div className="text-xs text-muted-foreground">
        Priority {row.priority} · {row.successCount} success
        {row.successCount === 1 ? '' : 'es'} · {row.failureCount} failure
        {row.failureCount === 1 ? '' : 's'}
      </div>
      {row.failureReason && label === 'failing' ? (
        <div className="text-xs font-mono bg-bg-subtle border border-border-subtle rounded px-2 py-1.5 break-words whitespace-pre-wrap">
          {row.failureReason}
        </div>
      ) : null}
    </li>
  );
}

/**
 * DeviceHealth — per-device health surface combining collector_health
 * (each gateway-side collector's poll status) with
 * connection_method_health (each (SSH/HTTPS/...) attempt's success
 * record). One backend round-trip drives both lists.
 *
 * Empty state when no rows exist yet — typical for a freshly
 * provisioned device before the gateway's 60s reporter has fired.
 */
export default function DeviceHealth({ deviceId }: DeviceHealthProps) {
  const { data, isLoading, isError, error } = useDeviceHealth(deviceId);

  if (isLoading) {
    return (
      <div className="space-y-4 pt-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load device health:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </AlertDescription>
      </Alert>
    );
  }

  const collectors = data?.collectors ?? [];
  const connectionMethods = data?.connectionMethods ?? [];

  if (collectors.length === 0 && connectionMethods.length === 0) {
    return (
      <div className="pt-4">
        <EmptyState
          icon={CircleDashed}
          title="No health data yet"
          description="The gateway uploads collector and connection-method health every 60 seconds. Once the first report lands you'll see per-collector status and per-method connectivity here."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      <section>
        <header className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold">
            Collectors{' '}
            <span className="text-xs text-muted-foreground font-normal">
              ({collectors.length})
            </span>
          </h2>
          <p className="text-xs text-muted-foreground">
            Per-poll execution status. Failing rows page on-call via{' '}
            <code>collector_health_check</code>.
          </p>
        </header>
        {collectors.length === 0 ? (
          <div className="text-xs text-muted-foreground">No collector has reported yet.</div>
        ) : (
          <div className="space-y-4">
            {orderedGateways(collectors).map((gw) => (
              <div key={gw.gatewayId}>
                <GatewayGroupHeader meta={gw} />
                <ul className="space-y-2">
                  {collectors
                    .filter((c) => c.gatewayId === gw.gatewayId)
                    .map((c) => (
                      <CollectorRow
                        key={`${c.gatewayId}:${c.collectorName}`}
                        row={c}
                        deviceId={deviceId}
                      />
                    ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <header className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold">
            Connection methods{' '}
            <span className="text-xs text-muted-foreground font-normal">
              ({connectionMethods.length})
            </span>
          </h2>
          <p className="text-xs text-muted-foreground">
            Per-method connect attempt history. Try priority order, top first.
          </p>
        </header>
        {connectionMethods.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            No connection method has been attempted yet.
          </div>
        ) : (
          <div className="space-y-4">
            {orderedGateways(connectionMethods).map((gw) => (
              <div key={gw.gatewayId}>
                <GatewayGroupHeader meta={gw} />
                <ul className="space-y-2">
                  {connectionMethods
                    .filter((m) => m.gatewayId === gw.gatewayId)
                    .map((m) => (
                      <ConnectionMethodRow key={m.deviceConnectionMethodId} row={m} />
                    ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
